import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Copy, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  PERIOD_BUCKETS,
  normalizePeriodBuckets,
} from '../../utils/periodizationPeriodBuckets';
import ValueSchemaManagerPanel from './ValueSchemaManagerPanel';

function valueKindLabel(t, valueKind) {
  const key = `periodization.assessmentManager.valueKinds.${valueKind}`;
  const tr = t(key);
  return tr !== key ? tr : valueKind;
}

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

function findUserCustomizationForGlobal(globalSet, sets) {
  const bySource = sets.find(
    (s) => s.scope === 'user' && Number(s.source_set_id) === Number(globalSet.id)
  );
  if (bySource) return bySource;
  const copyName = `${globalSet.name} (copy)`.toLowerCase();
  return sets.find(
    (s) => s.scope === 'user' && String(s.name || '').toLowerCase() === copyName
  );
}

export default function AssessmentSetManager({ open, onOpenChange, onChanged }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [valueSchemas, setValueSchemas] = useState([]);
  const [sets, setSets] = useState([]);
  const [editingSetId, setEditingSetId] = useState(null);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const emptyTypeForm = () => ({
    type_key: '',
    name: '',
    value_kind: 'scale_0_100',
    value_schema_id: '',
    category: 'custom',
    description: '',
    period_buckets: normalizePeriodBuckets(),
  });
  const [typeForm, setTypeForm] = useState(emptyTypeForm());
  const [newSetName, setNewSetName] = useState('');
  const [editSetName, setEditSetName] = useState('');
  const [bucketMatrix, setBucketMatrix] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingSetId, setDeletingSetId] = useState(null);
  const [deletingTypeId, setDeletingTypeId] = useState(null);

  const resetTypeForm = () => {
    setEditingTypeId(null);
    setTypeForm(emptyTypeForm());
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes, vsRes] = await Promise.all([
        api.get('/api/training/periodization/attribute-types'),
        api.get('/api/training/periodization/assessment-sets'),
        api.get('/api/training/periodization/value-schemas'),
      ]);
      setTypes(tRes.data.attribute_types || []);
      setSets(sRes.data.assessment_sets || []);
      setValueSchemas(vsRes.data.value_schemas || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const userTypes = types.filter((x) => x.scope === 'user' && !x.is_archived);
  const globalTypes = types.filter((x) => x.scope === 'global' && !x.is_archived);
  const editingSet = sets.find((s) => s.id === editingSetId);

  const allTypes = globalTypes.concat(userTypes);

  const syncBucketMatrixFromSet = (set) => {
    const next = {};
    for (const at of set?.attribute_types || []) {
      next[at.id] = normalizePeriodBuckets(at.period_buckets, at.period_buckets_allowed);
    }
    setBucketMatrix(next);
  };

  const startEditSet = (set) => {
    if (set.scope !== 'user') return;
    setEditingSetId(set.id);
    setEditSetName(set.name || '');
    setSelectedTypeIds((set.attribute_types || []).map((at) => at.id));
    syncBucketMatrixFromSet(set);
  };

  const openSetEditor = (set) => {
    setEditingSetId(set.id);
    setEditSetName(set.name || '');
    setSelectedTypeIds((set.attribute_types || []).map((at) => at.id));
    syncBucketMatrixFromSet(set);
  };

  const buildAttributeItemsPayload = () =>
    selectedTypeIds.map((typeId) => ({
      attribute_type_id: typeId,
      period_buckets: bucketMatrix[typeId] || normalizePeriodBuckets(allTypes.find((x) => x.id === typeId)?.period_buckets_allowed),
    }));

  const saveSetTypes = async () => {
    if (!editingSetId) return;
    setSaving(true);
    try {
      const res = await api.put(`/api/training/periodization/assessment-sets/${editingSetId}`, {
        name: editSetName.trim() || undefined,
        attribute_type_ids: selectedTypeIds,
        attribute_items: buildAttributeItemsPayload(),
      });
      const saved = res.data?.assessment_set;
      await load();
      if (saved?.id) {
        setEditingSetId(saved.id);
        setEditSetName(saved.name || editSetName);
        setSelectedTypeIds((saved.attribute_types || []).map((at) => at.id));
        syncBucketMatrixFromSet(saved);
      }
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const saveType = async () => {
    if (!typeForm.name.trim()) return;
    if (!editingTypeId && !typeForm.type_key.trim()) return;
    setSaving(true);
    try {
      if (editingTypeId) {
        await api.put(`/api/training/periodization/attribute-types/${editingTypeId}`, {
          name: typeForm.name.trim(),
          value_schema_id: typeForm.value_schema_id || undefined,
          category: typeForm.category,
          description: typeForm.description || null,
          period_buckets: typeForm.period_buckets,
        });
      } else {
        if (!typeForm.value_schema_id) {
          window.alert(t('periodization.valueSchema.required'));
          setSaving(false);
          return;
        }
        await api.post('/api/training/periodization/attribute-types', {
          type_key: typeForm.type_key.trim(),
          name: typeForm.name.trim(),
          value_schema_id: parseInt(typeForm.value_schema_id, 10),
          category: typeForm.category,
          description: typeForm.description || null,
          period_buckets: typeForm.period_buckets,
        });
      }
      resetTypeForm();
      await load();
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.typeSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const startEditType = (type) => {
    if (type.scope !== 'user') return;
    setEditingTypeId(type.id);
    setTypeForm({
      type_key: type.type_key,
      name: type.name || '',
      value_kind: type.value_kind || 'scale_0_100',
      value_schema_id: type.value_schema_id ? String(type.value_schema_id) : '',
      category: type.category || 'custom',
      description: type.description || '',
      period_buckets: normalizePeriodBuckets(type.period_buckets_allowed || type.period_buckets),
    });
  };

  const archiveType = async (type) => {
    if (type.scope !== 'user') return;
    if (!window.confirm(t('periodization.assessmentManager.deleteTypeConfirm', { name: type.name }))) return;
    setDeletingTypeId(type.id);
    try {
      await api.put(`/api/training/periodization/attribute-types/${type.id}`, { is_archived: true });
      if (editingTypeId === type.id) resetTypeForm();
      await load();
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.typeSaveFailed'));
    } finally {
      setDeletingTypeId(null);
    }
  };

  const duplicateTypeFromGlobal = async (type) => {
    const baseKey = `${type.type_key}_custom`.slice(0, 58);
    let typeKey = baseKey;
    let n = 1;
    while (types.some((x) => x.type_key === typeKey)) {
      typeKey = `${baseKey}_${n}`.slice(0, 64);
      n += 1;
    }
    setSaving(true);
    try {
      let schemaId = type.value_schema_id;
      if (!schemaId) {
        window.alert(t('periodization.valueSchema.required'));
        setSaving(false);
        return;
      }
      if (type.value_schema?.scope === 'global') {
        const dup = await api.post(`/api/training/periodization/value-schemas/${schemaId}/duplicate`);
        schemaId = dup.data?.value_schema?.id;
      }
      const res = await api.post('/api/training/periodization/attribute-types', {
        type_key: typeKey,
        name: `${type.name} (copy)`,
        value_schema_id: schemaId,
        category: 'custom',
        period_buckets: normalizePeriodBuckets(type.period_buckets_allowed || type.period_buckets),
      });
      await load();
      const created = res.data?.attribute_type;
      if (created?.id) {
        const refreshed = (await api.get('/api/training/periodization/attribute-types')).data?.attribute_types || [];
        const full = refreshed.find((x) => x.id === created.id) || created;
        startEditType(full);
      }
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.typeSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleTypeFormBucket = (bucket) => {
    setTypeForm((prev) => ({
      ...prev,
      period_buckets: {
        ...normalizePeriodBuckets(prev.period_buckets),
        [bucket]: !normalizePeriodBuckets(prev.period_buckets)[bucket],
      },
    }));
  };

  const duplicateSet = async (setId, options = {}) => {
    const { openEditor = false, name: customName } = options;
    const source = sets.find((s) => s.id === setId);
    setSaving(true);
    try {
      const res = await api.post(`/api/training/periodization/assessment-sets/${setId}/duplicate`, {
        name: customName || `${source?.name || 'Set'} (copy)`,
      });
      const created = res.data?.assessment_set;
      await load();
      if (openEditor && created?.id) {
        setEditingSetId(created.id);
        setEditSetName(created.name || '');
        setSelectedTypeIds((created.attribute_types || []).map((at) => at.id));
        const nextBuckets = {};
        for (const at of created.attribute_types || []) {
          nextBuckets[at.id] = normalizePeriodBuckets(at.period_buckets, at.period_buckets_allowed);
        }
        setBucketMatrix(nextBuckets);
      }
      onChanged?.();
      return created;
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.duplicateFailed'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const customizeSet = async (set) => {
    if (set.scope === 'user') {
      openSetEditor(set);
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/api/training/periodization/assessment-sets/${set.id}/customize`);
      const opened = res.data?.assessment_set;
      await load();
      if (opened?.id) {
        openSetEditor(opened);
      }
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.duplicateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const createSet = async () => {
    if (!newSetName.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/api/training/periodization/assessment-sets', { name: newSetName.trim() });
      const created = res.data?.assessment_set;
      setNewSetName('');
      await load();
      if (created?.id) {
        openSetEditor(created);
      }
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleTypeInSet = (typeId) => {
    setSelectedTypeIds((prev) => {
      if (prev.includes(typeId)) {
        setBucketMatrix((bm) => {
          const next = { ...bm };
          delete next[typeId];
          return next;
        });
        return prev.filter((id) => id !== typeId);
      }
      const type = allTypes.find((x) => x.id === typeId);
      setBucketMatrix((bm) => ({
        ...bm,
        [typeId]: normalizePeriodBuckets(type?.period_buckets_allowed),
      }));
      return [...prev, typeId];
    });
  };

  const toggleBucketForType = (typeId, bucket, allowed) => {
    if (!allowed[bucket]) return;
    setBucketMatrix((prev) => {
      const current = normalizePeriodBuckets(prev[typeId], allowed);
      return {
        ...prev,
        [typeId]: { ...current, [bucket]: !current[bucket] },
      };
    });
  };

  const deleteSet = async (set) => {
    if (set.scope !== 'user') return;
    if (!window.confirm(t('periodization.assessmentManager.deleteConfirm', { name: set.name }))) return;
    setDeletingSetId(set.id);
    try {
      await api.delete(`/api/training/periodization/assessment-sets/${set.id}`);
      if (editingSetId === set.id) {
        setEditingSetId(null);
        setSelectedTypeIds([]);
      }
      await load();
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.deleteFailed'));
    } finally {
      setDeletingSetId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[min(92vw,720px)] max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('periodization.assessmentManager.title')}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='h-8 w-8 animate-spin text-primary' />
          </div>
        ) : (
          <Tabs defaultValue='sets'>
            <TabsList className='mb-4'>
              <TabsTrigger value='sets'>{t('periodization.assessmentManager.setsTab')}</TabsTrigger>
              <TabsTrigger value='valueSchemas'>{t('periodization.valueSchema.tab')}</TabsTrigger>
              <TabsTrigger value='types'>{t('periodization.assessmentManager.typesTab')}</TabsTrigger>
            </TabsList>

            <TabsContent value='sets' className='space-y-4'>
              <p className='text-xs text-muted-foreground'>{t('periodization.assessmentManager.builtinHint')}</p>
              <div className='flex gap-2'>
                <Input
                  value={newSetName}
                  onChange={(e) => setNewSetName(e.target.value)}
                  placeholder={t('periodization.assessmentManager.newSetName')}
                  className='flex-1'
                />
                <Button type='button' onClick={createSet} disabled={saving}>
                  {t('periodization.assessmentManager.createSet')}
                </Button>
              </div>
              <ul className='space-y-2'>
                {sets.map((set) => {
                  const existingCustom =
                    set.scope === 'global' ? findUserCustomizationForGlobal(set, sets) : null;
                  const isEditing = editingSetId === set.id;
                  return (
                  <li
                    key={set.id}
                    className={cn(
                      'rounded-lg border border-border p-3',
                      isEditing && 'border-primary/40 bg-primary/5',
                      set.scope === 'user' && 'cursor-pointer hover:bg-muted/30'
                    )}
                    onClick={set.scope === 'user' ? () => startEditSet(set) : undefined}
                    onKeyDown={
                      set.scope === 'user'
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              startEditSet(set);
                            }
                          }
                        : undefined
                    }
                    role={set.scope === 'user' ? 'button' : undefined}
                    tabIndex={set.scope === 'user' ? 0 : undefined}
                  >
                    <div className='flex items-start justify-between gap-2'>
                      <div>
                        <p className='text-sm font-medium'>{set.name}</p>
                        <p className='text-xs text-muted-foreground'>
                          {set.attribute_count ?? set.attribute_types?.length ?? 0}{' '}
                          {t('periodization.assessmentManager.attributes')}
                          {set.scope === 'global' ? (
                            <Badge variant='secondary' className='ml-2 text-[10px]'>
                              {t('periodization.assessmentManager.global')}
                            </Badge>
                          ) : (
                            <Badge variant='outline' className='ml-2 text-[10px]'>
                              {t('periodization.assessmentManager.custom')}
                            </Badge>
                          )}
                        </p>
                      </div>
                      <div
                        className='flex shrink-0 flex-wrap justify-end gap-1'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {set.scope === 'global' ? (
                          <>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              onClick={() => customizeSet(set)}
                              disabled={saving}
                            >
                              {existingCustom
                                ? t('common.edit')
                                : t('periodization.assessmentManager.customize')}
                            </Button>
                            <Button
                              type='button'
                              size='icon'
                              variant='ghost'
                              className='h-8 w-8'
                              onClick={() => duplicateSet(set.id)}
                              disabled={saving}
                              title={t('periodization.assessmentManager.duplicate')}
                            >
                              <Copy className='h-4 w-4' />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button type='button' size='sm' variant='outline' onClick={() => startEditSet(set)}>
                              {t('common.edit')}
                            </Button>
                            <Button
                              type='button'
                              size='icon'
                              variant='ghost'
                              className='h-8 w-8 text-destructive hover:text-destructive'
                              onClick={() => deleteSet(set)}
                              disabled={deletingSetId === set.id}
                              title={t('common.delete')}
                            >
                              {deletingSetId === set.id ? (
                                <Loader2 className='h-4 w-4 animate-spin' />
                              ) : (
                                <Trash2 className='h-4 w-4' />
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>

              {editingSet && (
                <div className='rounded-lg border border-primary/30 bg-muted/30 p-3 space-y-3'>
                  <div>
                    <Label className='text-xs'>{t('periodization.assessmentManager.editSet')}</Label>
                    <Input
                      value={editSetName}
                      onChange={(e) => setEditSetName(e.target.value)}
                      className='mt-1'
                    />
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    {t('periodization.assessmentManager.editAttributesHint')}
                  </p>
                  <div className='max-h-48 overflow-y-auto space-y-2'>
                    {allTypes.map((type) => (
                      <div key={type.id} className='flex items-center justify-between gap-2 text-xs py-1'>
                        <span>{attrLabel(t, type.type_key, type.name)}</span>
                        <Switch
                          checked={selectedTypeIds.includes(type.id)}
                          onCheckedChange={() => toggleTypeInSet(type.id)}
                        />
                      </div>
                    ))}
                  </div>

                  {selectedTypeIds.length > 0 && (
                    <div className='space-y-2'>
                      <p className='text-xs font-medium'>{t('periodization.assessmentManager.bucketMatrixTitle')}</p>
                      <p className='text-[10px] text-muted-foreground'>
                        {t('periodization.assessmentManager.bucketMatrixHint')}
                      </p>
                      <div className='overflow-x-auto rounded-md border border-border'>
                        <table className='w-full min-w-[420px] text-[10px]'>
                          <thead>
                            <tr className='border-b border-border bg-muted/40'>
                              <th className='p-2 text-left font-medium'>{t('periodization.attribute')}</th>
                              {PERIOD_BUCKETS.map((bucket) => (
                                <th key={bucket} className='p-2 text-center font-medium capitalize'>
                                  {t(`periodization.assessmentManager.buckets.${bucket}`)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedTypeIds.map((typeId) => {
                              const type = allTypes.find((x) => x.id === typeId);
                              if (!type) return null;
                              const allowed = normalizePeriodBuckets(type.period_buckets_allowed);
                              const enabled = normalizePeriodBuckets(bucketMatrix[typeId], allowed);
                              return (
                                <tr key={typeId} className='border-b border-border/60'>
                                  <td className='p-2 font-medium'>
                                    {attrLabel(t, type.type_key, type.name)}
                                  </td>
                                  {PERIOD_BUCKETS.map((bucket) => (
                                    <td key={bucket} className='p-2 text-center'>
                                      <input
                                        type='checkbox'
                                        className='h-3.5 w-3.5 accent-primary'
                                        checked={!!enabled[bucket]}
                                        disabled={!allowed[bucket]}
                                        onChange={() => toggleBucketForType(typeId, bucket, allowed)}
                                        aria-label={t('periodization.assessmentManager.bucketToggleAria', {
                                          type: type.name,
                                          bucket: t(`periodization.assessmentManager.buckets.${bucket}`),
                                        })}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div className='flex gap-2 justify-end'>
                    <Button type='button' size='sm' variant='ghost' onClick={() => setEditingSetId(null)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type='button' size='sm' onClick={saveSetTypes} disabled={saving}>
                      {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('common.save')}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value='valueSchemas' className='space-y-4'>
              <ValueSchemaManagerPanel
                schemas={valueSchemas}
                onReload={load}
                saving={saving}
                setSaving={setSaving}
              />
            </TabsContent>

            <TabsContent value='types' className='space-y-4'>
              <p className='text-xs text-muted-foreground'>
                {t('periodization.assessmentManager.builtinTypesHint')}
              </p>
              <div className='rounded-lg border border-border p-3 space-y-3'>
                <p className='text-sm font-medium'>
                  {editingTypeId
                    ? t('periodization.assessmentManager.editType')
                    : t('periodization.assessmentManager.createType')}
                </p>
                <div className='grid gap-2 sm:grid-cols-2'>
                  <div>
                    <Label className='text-xs'>{t('periodization.assessmentManager.typeKey')}</Label>
                    <Input
                      value={typeForm.type_key}
                      onChange={(e) => setTypeForm((p) => ({ ...p, type_key: e.target.value }))}
                      placeholder='my_metric'
                      className='mt-1 font-mono text-xs'
                      disabled={!!editingTypeId}
                      readOnly={!!editingTypeId}
                    />
                    {!!editingTypeId && (
                      <p className='mt-1 text-[10px] text-muted-foreground'>
                        {t('periodization.assessmentManager.typeKeyReadonly')}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className='text-xs'>{t('periodization.assessmentManager.typeName')}</Label>
                    <Input
                      value={typeForm.name}
                      onChange={(e) => setTypeForm((p) => ({ ...p, name: e.target.value }))}
                      className='mt-1'
                    />
                  </div>
                  <div>
                    <Label className='text-xs'>{t('periodization.valueSchema.pickSet')}</Label>
                    <Select
                      value={typeForm.value_schema_id ? String(typeForm.value_schema_id) : ''}
                      onValueChange={(v) => setTypeForm((p) => ({ ...p, value_schema_id: v }))}
                    >
                      <SelectTrigger className='mt-1'><SelectValue placeholder={t('periodization.valueSchema.pickSet')} /></SelectTrigger>
                      <SelectContent>
                        {valueSchemas.filter((s) => !s.is_archived).map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>
                            {s.name} ({s.scope === 'global' ? t('periodization.assessmentManager.global') : t('periodization.valueSchema.yours')})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='flex items-end gap-2'>
                    {editingTypeId ? (
                      <>
                        <Button type='button' variant='ghost' onClick={resetTypeForm} disabled={saving} className='flex-1'>
                          {t('common.cancel')}
                        </Button>
                        <Button type='button' onClick={saveType} disabled={saving} className='flex-1'>
                          {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.assessmentManager.saveType')}
                        </Button>
                      </>
                    ) : (
                      <Button type='button' onClick={saveType} disabled={saving} className='w-full'>
                        {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.assessmentManager.createType')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className='space-y-2'>
                  <p className='text-xs font-medium'>{t('periodization.assessmentManager.bucketMatrixTitle')}</p>
                  <div className='flex flex-wrap gap-3'>
                    {PERIOD_BUCKETS.map((bucket) => (
                      <label key={bucket} className='flex items-center gap-2 text-xs capitalize'>
                        <input
                          type='checkbox'
                          className='h-3.5 w-3.5 accent-primary'
                          checked={!!normalizePeriodBuckets(typeForm.period_buckets)[bucket]}
                          onChange={() => toggleTypeFormBucket(bucket)}
                        />
                        {t(`periodization.assessmentManager.buckets.${bucket}`)}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <p className='text-xs font-medium text-muted-foreground mb-2'>
                  {t('periodization.assessmentManager.catalog')}
                </p>
                <ul className='space-y-1 max-h-56 overflow-y-auto'>
                  {globalTypes.map((type) => (
                    <li key={type.id} className='text-xs flex justify-between gap-2 py-1 border-b border-border/50'>
                      <span>{attrLabel(t, type.type_key, type.name)}</span>
                      <div className='flex flex-wrap justify-end items-center gap-1'>
                        {PERIOD_BUCKETS.filter((b) => normalizePeriodBuckets(type.period_buckets_allowed)[b]).map((b) => (
                          <Badge key={b} variant='outline' className='text-[9px] capitalize'>
                            {t(`periodization.assessmentManager.buckets.${b}`)}
                          </Badge>
                        ))}
                        <Badge variant='secondary' className='text-[10px]'>
                          {t('periodization.assessmentManager.global')}
                        </Badge>
                        <Badge variant='outline' className='text-[10px]'>
                          {type.value_schema?.name || valueKindLabel(t, type.value_kind)}
                        </Badge>
                        <Button
                          type='button'
                          size='icon'
                          variant='ghost'
                          className='h-7 w-7'
                          onClick={() => duplicateTypeFromGlobal(type)}
                          disabled={saving}
                          title={t('periodization.assessmentManager.duplicateType')}
                        >
                          <Copy className='h-3.5 w-3.5' />
                        </Button>
                      </div>
                    </li>
                  ))}
                  {userTypes.map((type) => {
                    const isEditing = editingTypeId === type.id;
                    return (
                      <li
                        key={type.id}
                        className={cn(
                          'text-xs flex justify-between gap-2 py-1 border-b border-border/50 rounded px-1 -mx-1',
                          isEditing && 'border-primary/40 bg-primary/5',
                          'cursor-pointer hover:bg-muted/30'
                        )}
                        onClick={() => startEditType(type)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            startEditType(type);
                          }
                        }}
                        role='button'
                        tabIndex={0}
                      >
                        <span>{type.name}</span>
                        <div
                          className='flex flex-wrap justify-end items-center gap-1'
                          onClick={(e) => e.stopPropagation()}
                        >
                          {PERIOD_BUCKETS.filter((b) => normalizePeriodBuckets(type.period_buckets_allowed || type.period_buckets)[b]).map((b) => (
                            <Badge key={b} variant='outline' className='text-[9px] capitalize'>
                              {t(`periodization.assessmentManager.buckets.${b}`)}
                            </Badge>
                          ))}
                          <Badge variant='outline' className='text-[10px]'>{t('periodization.assessmentManager.custom')}</Badge>
                          <Badge variant='outline' className='text-[10px]'>
                          {type.value_schema?.name || valueKindLabel(t, type.value_kind)}
                        </Badge>
                          <Button type='button' size='sm' variant='outline' className='h-7 px-2' onClick={() => startEditType(type)}>
                            {t('common.edit')}
                          </Button>
                          <Button
                            type='button'
                            size='icon'
                            variant='ghost'
                            className='h-7 w-7 text-destructive hover:text-destructive'
                            onClick={() => archiveType(type)}
                            disabled={deletingTypeId === type.id}
                            title={t('common.delete')}
                          >
                            {deletingTypeId === type.id ? (
                              <Loader2 className='h-3.5 w-3.5 animate-spin' />
                            ) : (
                              <Trash2 className='h-3.5 w-3.5' />
                            )}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
